import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Newspaper, Play } from 'lucide-react';
import VideoPlayer from '../components/platform/VideoPlayer';
import { SiteLayout, useDocumentTitle } from '../components/platform/SiteLayout';
import { Container, Heading, Card, Spinner, EmptyState, ErrorState, PlainText } from '../components/platform/ui';
import useAsync from '../hooks/useAsync';
import { platformApi } from '../services/platformApi';
import { formatDate } from '../lib/pay';
import { LLink, useLang } from '../lib/lang';

export const NewsPage = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useLang();
  useDocumentTitle(t('pl.newsPage.title'));
  const { data, loading, error, reload } = useAsync(platformApi.news, [i18n.language]);
  return (
    <SiteLayout>
      <Container className="py-12 max-w-4xl">
        <Heading level={1} className="text-4xl sm:text-5xl mb-8">{t('pl.newsPage.title')}</Heading>
        {loading ? <Spinner /> : error ? <ErrorState onRetry={reload} /> : data.length === 0 ? (
          <EmptyState icon={Newspaper} title={t('pl.newsPage.emptyTitle')} text={t('pl.newsPage.emptyText')} />
        ) : (
          <div className="space-y-5">
            {data.map((n) => (
              <Card key={n.id} className="sm:flex gap-5">
                {(n.cover_url || n.has_video) && (
                  <LLink to={`/news/${n.slug}`} tabIndex={-1} aria-hidden="true"
                    className="relative block w-full sm:w-52 h-36 shrink-0 mb-3 sm:mb-0 overflow-hidden bg-[#0d0b08]">
                    {n.cover_url && <img src={n.cover_url} alt="" loading="lazy" className="w-full h-full object-cover" />}
                    {n.has_video && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                        <span className="w-12 h-12 rounded-full bg-[#C41E3A] text-white flex items-center justify-center shadow-lg"><Play size={22} fill="currentColor" /></span>
                      </span>
                    )}
                  </LLink>
                )}
                <div>
                  <p className="text-xs text-gray-500 mb-1">{formatDate(n.published_at, lang)}</p>
                  <LLink to={`/news/${n.slug}`} className="hover:text-[#C41E3A]"><h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{n.title}</h2></LLink>
                  {n.summary && <p className="text-gray-700">{n.summary}</p>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Container>
    </SiteLayout>
  );
};

export const NewsPostPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { lang } = useLang();
  const { data, loading, error, reload } = useAsync(() => platformApi.newsPost(slug), [slug, i18n.language]);
  useDocumentTitle(data?.title);
  const notFound = error?.response?.status === 404;
  return (
    <SiteLayout>
      <Container className="py-12 max-w-3xl">
        <LLink to="/news" className="underline text-sm">{t('pl.newsPage.all')}</LLink>
        {loading ? <Spinner /> : error ? <ErrorState text={notFound ? t('pl.newsPage.notFound') : t('pl.newsPage.loadError')} onRetry={notFound ? undefined : reload} /> : (
          <article className="mt-4 space-y-5">
            <p className="text-sm text-gray-500">{formatDate(data.published_at, lang)}</p>
            <Heading level={1} className="text-4xl">{data.title}</Heading>
            {data.video_url
              ? <VideoPlayer url={data.video_url} aspect={data.video_aspect || '16:9'} poster={data.cover_url} title={data.title} />
              : data.cover_url && <img src={data.cover_url} alt="" className="w-full border-2 border-black" />}
            <PlainText text={data.body} />
          </article>
        )}
      </Container>
    </SiteLayout>
  );
};
